import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { useMutation, useQuery } from "@tanstack/react-query";
import { memo, useContext, useEffect, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";
import { TeamContext } from "../../team";

interface TemplateWithMalomoLink {
  templateId: string;
  templateName: string;
  klaviyoUrl: string;
}

export const ScanKlaviyoTemplatesCard = memo(
  function ScanKlaviyoTemplatesCard() {
    const team = useContext(TeamContext);
    const rpcClient = useContext(RedoAdminRpcClientContext);
    const [isPolling, setIsPolling] = useState(false);
    const [hasShownSuccessToast, setHasShownSuccessToast] = useState(false);

    // Query to load cached results on mount (startScan=false)
    const cachedResultsQuery = useQuery({
      queryKey: ["scanKlaviyoTemplates", team?._id],
      queryFn: async () => {
        if (!team || !rpcClient) {
          throw new Error("Team or RPC client not available");
        }
        return rpcClient.scanKlaviyoTemplatesForMalomoLinks({
          teamId: team._id,
          startScan: false,
        });
      },
      enabled: !!team && !!rpcClient,
      refetchOnWindowFocus: false,
      refetchInterval: isPolling ? 2000 : false, // Poll every 2 seconds when scanning
    });

    // Derive results directly from query data
    const results: TemplateWithMalomoLink[] =
      cachedResultsQuery.data?.scanned && cachedResultsQuery.data?.templates
        ? cachedResultsQuery.data.templates
            .filter((t) => t.hasMalomoLinks)
            .map((t) => ({
              templateId: t.id,
              templateName: t.name,
              klaviyoUrl: `https://www.klaviyo.com/email-template-editor/${t.id}`,
            }))
        : [];

    // Handle polling completion
    useEffect(() => {
      if (
        isPolling &&
        cachedResultsQuery.data?.scanned &&
        !hasShownSuccessToast
      ) {
        setIsPolling(false);
        setHasShownSuccessToast(true);
        const templatesWithLinks = results.length;
        toast(
          `Scanned ${cachedResultsQuery.data.templateCount} template(s). Found ${templatesWithLinks} with Malomo links.`,
          { variant: "success" },
        );
      }
    }, [
      isPolling,
      cachedResultsQuery.data,
      hasShownSuccessToast,
      results.length,
    ]);

    const scanMutation = useMutation({
      mutationFn: async () => {
        if (!team || !rpcClient) {
          throw new Error("Team or RPC client not available");
        }
        return rpcClient.scanKlaviyoTemplatesForMalomoLinks({
          teamId: team._id,
          startScan: true,
        });
      },
      onSuccess: (response) => {
        if (response?.scanned && response?.templates) {
          // Workflow completed immediately (cached result)
          const templatesWithLinks = response.templates.filter(
            (t) => t.hasMalomoLinks,
          );
          toast(
            `Scanned ${response.templateCount} template(s). Found ${templatesWithLinks.length} with Malomo links.`,
            { variant: "success" },
          );
          setHasShownSuccessToast(true);
        } else if (!response?.scanned) {
          // Workflow started, begin polling
          toast("Scan started. Checking for results...", { variant: "info" });
          setIsPolling(true);
          setHasShownSuccessToast(false);
        }
      },
      onError: (error) => {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error";
        toast(`Failed to scan templates: ${errorMessage}`, {
          variant: "error",
        });
        setIsPolling(false);
      },
    });

    return (
      <Card title="Scan Klaviyo templates for Malomo links">
        <Flex dir="column" gap="lg">
          <Text fontSize="sm">
            Scan all Klaviyo templates for this team to identify templates that
            contain Malomo tracking links. This helps identify templates that
            need to be updated when migrating to Order Tracking V2.
          </Text>

          {results.length > 0 && (
            <div
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: "var(--border-radius-lg)",
                overflow: "hidden",
              }}
            >
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr
                    style={{
                      backgroundColor: "var(--redo-colors-gray-neutral-50)",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    <th
                      style={{
                        padding: "var(--redo-spacing-lg)",
                        textAlign: "left",
                        fontSize: "var(--redo-font-size-sm)",
                        fontWeight: "var(--redo-font-weight-semibold)",
                      }}
                    >
                      Template Name
                    </th>
                    <th
                      style={{
                        padding: "var(--redo-spacing-lg)",
                        textAlign: "left",
                        fontSize: "var(--redo-font-size-sm)",
                        fontWeight: "var(--redo-font-weight-semibold)",
                      }}
                    >
                      Link
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((template) => (
                    <tr
                      key={template.templateId}
                      style={{ borderBottom: "1px solid #e5e7eb" }}
                    >
                      <td
                        style={{
                          padding: "var(--redo-spacing-lg)",
                          fontSize: "var(--redo-font-size-sm)",
                        }}
                      >
                        {template.templateName}
                      </td>
                      <td
                        style={{
                          padding: "var(--redo-spacing-lg)",
                          fontSize: "var(--redo-font-size-sm)",
                        }}
                      >
                        <a
                          href={template.klaviyoUrl}
                          rel="noopener noreferrer"
                          style={{
                            color: "var(--redo-colors-blue-dark-600)",
                            textDecoration: "none",
                          }}
                          target="_blank"
                        >
                          View in Klaviyo
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <RedoButton
            disabled={
              scanMutation.isPending || isPolling || !team || !rpcClient
            }
            hierarchy="primary"
            onClick={() => scanMutation.mutate()}
            size="sm"
            text={
              scanMutation.isPending || isPolling
                ? "Scanning..."
                : "Scan Templates"
            }
          />
        </Flex>
      </Card>
    );
  },
);
